import { useMemo } from "react";

import { useAppUI } from "contexts/UIContext";

import { MenuItem, sidebarLinks } from "./Sidebar/links";

export interface NavItem {
  id: string;
  label: string;
  description: string;
  section: string;
  action: string;
  url?: string;
}

/**
 * The permission-filtered navigation tree — the single source of truth for
 * "what pages can this user see". Both the Sidebar menu and the navbar page
 * search derive from it, so they can never diverge.
 */
export function useFilteredNavTree(): MenuItem[] {
  const { user } = useAppUI();

  return useMemo(() => {
    const granted = Object.keys(user?.permissions || {});
    const hasPermission = (userRight?: string | string[]): boolean =>
      userRight === undefined ||
      (Array.isArray(userRight) ? userRight : [userRight]).every((right) =>
        granted.includes(right)
      );

    return sidebarLinks
      .filter((item) => hasPermission(item.userRight))
      .map((item) => ({
        ...item,
        links: item.links.filter((link) => hasPermission(link.userRight)),
      }))
      .filter((item) => item.links.length > 0);
  }, [user]);
}

export function useNavItems(): NavItem[] {
  const tree = useFilteredNavTree();

  return useMemo(
    () =>
      tree.flatMap((item) =>
        item.links.map((link) => ({
          id: `${item.id}::${link.action}`,
          label: link.name,
          description: link.description,
          section: item.header,
          action: link.action,
          url: link.url,
        }))
      ),
    [tree]
  );
}
